import { defineStore } from "pinia";
import { StoryboardDialogTypes } from "@/modules/storyboards/types";
import { IdType, Storyboard, StoryboardRevision } from "@/modules/types";

const initialState = {
  isSyndicationDialogOpen: false,
  isSidePanelExpanded: true,
  dialogs: {
    delete: false,
    deleteVideo: false,
    deleteDocument: false,
    deleteLocalization: false,
    createNew: false,
    edit: false,
    uploadFile: false,
    createNewLocalization: false,
  } as Record<StoryboardDialogTypes, boolean>,
  dialogsData: {
    uploadFiles: {
      storyboardId: undefined as string | undefined | IdType,
      storyboardType: "" as string,
    },
  },
  previewMobile: false,
  activeElementName: "",
  storyboardData: {} as Storyboard,
  storyboardRevision: undefined as StoryboardRevision | undefined,
  currentTranslationTotalWords: 0,
  currentBacktranslationTotalWords: 0,
  aiTasks: [] as any[],
  isHistoryPanelOpen: false,
  isVideoPanelOpen: false,
  isAiSuggestionPanelOpen: false,
  isAiSuggestionActive: false,
  aiSuggestionSelectedRow: 0,
  aiSuggestionSelectedType: "",
  aiSuggestionSelectedId: "",
  aiSuggestionSelectedText: "",
  isSearchPanelOpen: false,
};

export const useStoryboardsStore = defineStore("ecca-storyboards", {
  state: () => ({ ...initialState }),
  getters: {
    getStoryboardAITaskById: (state) => {
      return (id: string) =>
        state.aiTasks.find((task) => {
          if (task.id === id) {
            return task;
          }
        });
    },
    isRevisionVisible: (state) => {
      return !!state.storyboardRevision;
    },
  },
  actions: {
    changeDialogStatus(
      type: StoryboardDialogTypes,
      value: boolean,
      stack: boolean = false,
    ) {
      if (value && !stack) this.resetDialogStatus();
      this.dialogs[type] = value;
    },
    showDialog(type: StoryboardDialogTypes, stack?: boolean) {
      this.changeDialogStatus(type, true, stack);
    },
    hideDialog(type: StoryboardDialogTypes) {
      this.changeDialogStatus(type, false);
    },
    resetDialogStatus() {
      this.dialogs = {
        ...initialState.dialogs,
      };
    },
    resetSidePanelState() {
      this.isSidePanelExpanded = initialState.isSidePanelExpanded;
    },
    setSidePanelState(payload: boolean) {
      this.isSidePanelExpanded = payload;
    },
    setStoryboardData(payload: Storyboard) {
      this.storyboardData = payload;
    },
    setStoryboardRevision(payload?: StoryboardRevision) {
      this.storyboardRevision = payload;
    },
    resetStoryboardRevision() {
      this.storyboardRevision = initialState.storyboardRevision;
    },
    setTranslationTotalWords(words: number) {
      this.currentTranslationTotalWords = words;
    },
    setBacktranslationTotalWords(words: number) {
      this.currentBacktranslationTotalWords = words;
    },
    addStoryboardAITask(item: any) {
      const task = { ...item, visible: true };
      this.aiTasks.push(task);
    },
    async setStoryboardAITaskSuccess(id: IdType, isSuccess: boolean) {
      return new Promise((resolve) => {
        const index = this.aiTasks.findIndex((item: any) => item.id === id);
        if (index !== -1) {
          this.aiTasks[index].isAiGenerationSuccess = isSuccess;
        }
        resolve(true);
      });
    },
    removeStoryboardAITask(id: IdType) {
      const index = this.aiTasks.findIndex((item: any) => item.id === id);
      if (index !== -1) {
        this.aiTasks.splice(index, 1);
      }
    },
    setHistoryPanel(payload: boolean) {
      this.isHistoryPanelOpen = payload;
    },
    setVideoPanel(payload: boolean) {
      this.isVideoPanelOpen = payload;
    },
    setAiSuggestionPanel(payload: boolean) {
      this.isAiSuggestionPanelOpen = payload;
    },
    resetAISuggestion() {
      this.isAiSuggestionActive = false;
      this.aiSuggestionSelectedRow = 0;
      this.aiSuggestionSelectedType = "";
      this.aiSuggestionSelectedId = "";
      this.aiSuggestionSelectedText = "";
    },
    setSearchPanel(payload: boolean) {
      this.isSearchPanelOpen = payload;
    },
  },
});
